import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Breadcrumbs, Grid } from '@mui/material';
import { Storage as StorageIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useNotification } from '../../../components/Notification';
import Table from '../../../components/Table';
import DateRangePicker from '../../../components/DateRangePicker';
import { formatDateTimeWithCulture } from '../../../utils/dateUtils';
import {
    fetchInit,
    fetchSystemLogs,
    selectSystemLogs,
    selectSystemLogsLoading,
    selectSystemLogsError,
    selectSystemLogsTotal,
    selectSystemLogsPage,
    selectSystemLogsPageSize,
    selectSystemLogsSortColumn,
    selectSystemLogsSortDirection,
    selectSystemLogsTypes,
    selectSystemLogsActions,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    clearError,
} from '../../System/SystemLogs/slice';

function BackupLogs() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const systemLogs = useSelector(selectSystemLogs);
    const loading = useSelector(selectSystemLogsLoading);
    const error = useSelector(selectSystemLogsError);
    const total = useSelector(selectSystemLogsTotal);
    const page = useSelector(selectSystemLogsPage);
    const pageSize = useSelector(selectSystemLogsPageSize);
    const sortColumn = useSelector(selectSystemLogsSortColumn);
    const sortDirection = useSelector(selectSystemLogsSortDirection);
    const types = useSelector(selectSystemLogsTypes);
    const actions = useSelector(selectSystemLogsActions);

    // Local state for date range filter
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // Fetch init data on mount
    useEffect(() => {
        dispatch(fetchInit());
    }, [dispatch]);

    // Fetch system logs with types=18 when pagination/sorting/date change
    useEffect(() => {
        const params = {
            page,
            pageSize,
            sortColumn: sortColumn,
            sortDirection: sortDirection,
            types: [18], // Always filter by type 18
        };

        // Add date filters if provided
        if (fromDate) {
            params.fromDate = `${fromDate}T00:00:00`;
        }
        if (toDate) {
            params.toDate = `${toDate}T23:59:59`;
        }

        dispatch(fetchSystemLogs(params));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, fromDate, toDate]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Handle pagination
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    const handlePageSizeChange = (newSize) => {
        dispatch(setPageSize(newSize));
    };

    // Handle sorting
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    // Handle date range filter
    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
        dispatch(setPage(1)); // Reset to first page
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
        dispatch(setPage(1)); // Reset to first page
    };

    // Table columns
    const columns = [
        {
            key: 'createdOn',
            label: t('systemLogs.table.createdOn'),
            align: 'left',
            render: (value) => formatDateTimeWithCulture(value, i18n.language),
        },
        {
            key: 'type',
            label: t('systemLogs.table.type'),
            render: (value) => types[value] || value,
        },
        {
            key: 'action',
            label: t('systemLogs.table.action'),
            render: (value) => actions[value] || value,
        },
        { key: 'content', label: t('systemLogs.table.content') },
        {
            key: 'detail',
            label: t('systemLogs.table.detail'),
            render: (value) => (
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                    {value}
                </Typography>
            ),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StorageIcon fontSize="small" />
                        <Typography variant="body2">{t('backup.breadcrumb.backup')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('backup.backupLogs.breadcrumb')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('backup.backupLogs.title')}
                    </Typography>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <DateRangePicker
                                label={t('backupLogs.filter.dateRange')}
                                fromValue={fromDate}
                                toValue={toDate}
                                onFromChange={handleFromDateChange}
                                onToChange={handleToDateChange}
                                id="filterDateRange"
                                name="filterDateRange"
                                data-testid="backupLogs-daterangepicker-filter"
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="backupLogs-table"
                        columns={columns}
                        data={systemLogs}
                        selectable={false}
                        loading={loading}
                        emptyMessage={t('systemLogs.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                    />
                </Box>
            </Paper>
        </Box>
    );
}

export default BackupLogs;
